import { ComponentId, ComponentSlice, FloatingWindow, dom } from "@omegagrid/core";
import type { AppHost } from "./appHost";
import type { Container } from "../container/container";
import { CommandQuery, CommandWindow, CommandsAdapter, CommandsSection } from "@omegagrid/commands";
import { CommandProvider } from "../container/commands";

export class AppHostCommandAdapter extends CommandsAdapter {

	constructor(readonly container: Container) {
		super();
	}

	get providers() {
		return this.container.commands.providers;
	}

	getSections(_query?: CommandQuery): CommandsSection[] | Promise<CommandsSection[]> {
		return Array.from(this.providers.values());
	}

	async getGroups(query?: CommandQuery) {
		const provider = this.providers.get(query?.sectionId) ?? this.providers.values().next().value;
		if (!provider?.getGroups) return [];
		return provider.getGroups(query);
	}

	async getCommands(query?: CommandQuery) {
		const provider = this.providers.get(query?.sectionId) ?? this.providers.values().next().value;
		if (!provider?.getCommands) return [];
		return provider.getCommands(query);
	}

	getOptions(sectionId?: ComponentId) {
		const provider = this.providers.get(sectionId) ?? this.providers.values().next().value;
		if (!provider?.getOptions) return {};
		return provider.getOptions();
	}

	registerProvider(provider: CommandProvider) {
		this.providers.set(provider.id, provider);
	}

}

export class AppHostCommands extends ComponentSlice<AppHost> {

	private _adapter: AppHostCommandAdapter;
	private _firstOpen = true;

	get adapter() {
		if (!this._adapter) this._adapter = new AppHostCommandAdapter(this.component.container);
		return this._adapter;
	}

	constructor(host: AppHost) {
		super(host);
	}

	private _commands: CommandWindow;
	get commands() {
		return this._commands ?? (this._commands = dom.createElement<CommandWindow>('og-commands', {
			slot: 'content',
			showGroups: true,
			adapter: this.adapter,
			listeners: [
				['command.exec', () => this.close()],
				['command.close', () => this.close()]
			]
		}));
	}

	/** compact (mobile) host: the window covers the whole host, the list fills it and a close button stands in for the click outside */
	get compact() {
		return this.component.compact;
	}

	private _commandContainer: FloatingWindow;
	get commandContainer() {
		if (this._commandContainer) return this._commandContainer;
		this._commandContainer = dom.createElement<FloatingWindow>('og-floating-window', {
			style: {zIndex: '50000'},
			listeners: [['hide', () => {
				this.component.focus();
				// this.adapter?.reset();
			}]]
		});
		this._commandContainer.appendChild(this.commands);
		return this._commandContainer;
	}

	registerProvider(provider: CommandProvider) {
		this.adapter.registerProvider(provider);
	}

	/**
	 * Opens the window on the given section (the sections are the providers in registration order), or
	 * on the first one, with an empty filter. A changed section refetches on its own; the window keeps
	 * its list otherwise, unless a filter was typed. Before the first render the window has no sections
	 * yet: the index is applied once they arrive.
	 */
	open(section?: string) {
		this.commands.fill = this.commands.closable = this.compact;
		this.component.shadowRoot?.appendChild(this.commandContainer);
		this.commandContainer.show();
		this.updatePosition();

		const index = section ? Math.max(0, Array.from(this.adapter.providers.keys()).indexOf(section)) : 0;
		const filtered = !!this.commands.filterValue;
		this.commands.clearFilter(false);
		if (this.commands.activeSectionIndex !== index) this.commands.activeSectionIndex = index;
		else if (filtered) this.commands.updateCommands();
		this.commands.focus();
	}

	close() {
		this.commandContainer.hide();
		this.commandContainer.remove();
	}

	updatePosition() {
		this.commandContainer.fullscreen = this.compact;
		if (this.compact) return;

		const searchOffset = dom.getElementOffset(this.component.searchBar.bar, this.component);
		dom.setPosition(this.commandContainer, {l: searchOffset.left, t: 0});
		dom.setSize(this.commandContainer, {w: this.component.searchBar.offsetWidth});

		// dom.setSize(this.commandContainer, {w: 0.9 * this.component.clientWidth});
		// dom.setCenterPosition(this.commandContainer, this.component, {horizontal: true, vertical: false});
		// dom.setPosition(this.commandContainer, {t: 50});
	}

}
